import React from 'react';
import { BadgeCheck, Info } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { BADGE_COPY } from '@/data/legal';

interface Props {
  licenseVerified?: boolean | null;
  insuranceVerified?: boolean | null;
  backgroundVerified?: boolean | null;
  /** Show an explicit "not verified" note when nothing has been verified. */
  showUnverifiedNote?: boolean;
  className?: string;
}

/**
 * Renders trust badges ONLY when the platform has actually completed the
 * verification. Nothing here is inferred from user-submitted data — a license
 * number on file does NOT produce a badge.
 */
const VerificationBadges: React.FC<Props> = ({
  licenseVerified,
  insuranceVerified,
  backgroundVerified,
  showUnverifiedNote = false,
  className = '',
}) => {
  const { lang } = useI18n();
  const pick = (v: { en: string; es: string }) => (lang === 'es' ? v.es : v.en);

  const badges = [
    licenseVerified ? pick(BADGE_COPY.license) : null,
    insuranceVerified ? pick(BADGE_COPY.insurance) : null,
    backgroundVerified ? pick(BADGE_COPY.background) : null,
  ].filter(Boolean) as string[];

  if (badges.length === 0) {
    if (!showUnverifiedNote) return null;
    return (
      <p className={`flex items-start gap-1.5 text-xs text-slate-500 ${className}`}>
        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {pick(BADGE_COPY.none)}
      </p>
    );
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {badges.map((b) => (
        <span
          key={b}
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200"
        >
          <BadgeCheck className="h-3.5 w-3.5" /> {b}
        </span>
      ))}
    </div>
  );
};

export default VerificationBadges;
